import React, { useState, useRef, useCallback } from 'react';
import { createWorker } from 'tesseract.js';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Camera, 
  Loader2, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  Edit,
  Upload,
  Scan
} from 'lucide-react';
import { toast } from 'sonner';

// Country codes from MRZ to full names
const COUNTRY_MAP = {
  'FRA': 'FR', 'DEU': 'DE', 'GBR': 'GB', 'USA': 'US', 'ESP': 'ES',
  'ITA': 'IT', 'BEL': 'BE', 'CHE': 'CH', 'NLD': 'NL', 'PRT': 'PT',
  'CAN': 'CA', 'MAR': 'MA', 'DZA': 'DZ', 'TUN': 'TN', 'EGY': 'EG',
  'GBD': 'GB', 'D<<': 'DE', 'F<<': 'FR'
};

// Parse passport from visual zone (human-readable labels) + MRZ
const parsePassport = (text) => {
  console.log('Parsing passport text...');
  
  const result = {
    confidence: 0,
    first_name: '',
    last_name: '',
    birth_date: '',
    nationality: '',
    document_number: '',
    sex: ''
  };
  
  // Normalize text
  const normalized = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  
  const lines = normalized.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const fullText = normalized.toUpperCase();
  
  console.log('Lines:', lines);
  
  // === METHOD 1: Extract from visual zone (labels) ===
  
  // Look for "Nom/Surname" pattern
  // French passports have: "Nom/Surname" followed by the name
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const upperLine = line.toUpperCase();
    
    // Nom / Surname
    if (upperLine.includes('NOM') && (upperLine.includes('SURNAME') || upperLine.includes('NAME'))) {
      // The surname is likely on the next line or after a separator
      const nextLine = lines[i + 1];
      if (nextLine && nextLine.match(/^[A-Z][A-Z\s\-']+$/i) && !nextLine.includes('/')) {
        result.last_name = capitalizeWord(nextLine.trim());
        result.confidence += 25;
        console.log('Found surname from label:', result.last_name);
      }
    }
    
    // Prénoms / Given names
    if (upperLine.includes('PRÉNOM') || upperLine.includes('PRENOM') || upperLine.includes('GIVEN')) {
      const nextLine = lines[i + 1];
      if (nextLine && nextLine.match(/^[A-Z][A-Za-z\s,\-']+$/i) && !nextLine.includes('/')) {
        // Given names may be comma-separated
        result.first_name = nextLine
          .split(/[,\s]+/)
          .filter(n => n.length > 1)
          .map(n => capitalizeWord(n))
          .join(' ');
        result.confidence += 25;
        console.log('Found given names from label:', result.first_name);
      }
    }
    
    // Date de naissance / Date of birth
    if (upperLine.includes('NAISSANCE') || upperLine.includes('BIRTH')) {
      const nextLine = lines[i + 1];
      // Look for date pattern: DD MM YYYY or DD/MM/YYYY or DD.MM.YYYY
      const dateMatch = nextLine?.match(/(\d{1,2})[\s\.\/\-](\d{1,2})[\s\.\/\-](\d{4})/);
      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        result.birth_date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        result.confidence += 20;
        console.log('Found birth date from label:', result.birth_date);
      }
    }
    
    // Nationalité / Nationality
    if (upperLine.includes('NATIONALITÉ') || upperLine.includes('NATIONALITE') || upperLine.includes('NATIONALITY')) {
      const nextLine = lines[i + 1];
      if (nextLine) {
        const natMatch = nextLine.match(/fran[çc]aise|french|allemande|german|espagnole|spanish|italienne|italian|belge|belgian|suisse|swiss|marocaine|moroccan|am[ée]ricaine|american|britannique|british/i);
        if (natMatch) {
          const natMap = {
            'française': 'FR', 'francaise': 'FR', 'french': 'FR',
            'allemande': 'DE', 'german': 'DE',
            'espagnole': 'ES', 'spanish': 'ES',
            'italienne': 'IT', 'italian': 'IT',
            'belge': 'BE', 'belgian': 'BE',
            'suisse': 'CH', 'swiss': 'CH',
            'marocaine': 'MA', 'moroccan': 'MA',
            'américaine': 'US', 'americaine': 'US', 'american': 'US',
            'britannique': 'GB', 'british': 'GB'
          };
          result.nationality = natMap[natMatch[0].toLowerCase()] || 'FR';
          result.confidence += 10;
          console.log('Found nationality from label:', result.nationality);
        }
      }
    }
    
    // Passeport n° / Passport number
    if (upperLine.includes('PASSEPORT') && (upperLine.includes('N°') || upperLine.includes('NO'))) {
      // Look for passport number pattern (alphanumeric, 8-9 chars)
      const docMatch = line.match(/\b([A-Z0-9]{8,9})\b/i) || lines[i+1]?.match(/\b([A-Z0-9]{8,9})\b/i);
      if (docMatch) {
        result.document_number = docMatch[1].toUpperCase();
        result.confidence += 15;
        console.log('Found passport number from label:', result.document_number);
      }
    }
    
    // Sexe / Sex
    if (upperLine.includes('SEXE') || upperLine.includes('SEX')) {
      const sexMatch = line.match(/\b([MF])\b/) || lines[i+1]?.match(/\b([MF])\b/);
      if (sexMatch) {
        result.sex = sexMatch[1];
      }
    }
  }
  
  // === METHOD 2: Look for standalone patterns ===
  
  // If we didn't find surname, look for all-caps name (likely surname)
  if (!result.last_name) {
    const capsNameMatch = fullText.match(/\n([A-Z]{3,})\n/);
    if (capsNameMatch && !capsNameMatch[1].match(/PASSEPORT|PASSPORT|RÉPUBLIQUE|FRANCE|SURNAME|GIVEN/)) {
      result.last_name = capitalizeWord(capsNameMatch[1]);
      result.confidence += 15;
      console.log('Found surname from caps pattern:', result.last_name);
    }
  }
  
  // Look for passport number pattern in full text
  if (!result.document_number) {
    const docPatterns = fullText.match(/\b(\d{2}[A-Z]{2}\d{5,6})\b/); // French format: 22DE92501
    if (docPatterns) {
      result.document_number = docPatterns[1];
      result.confidence += 10;
      console.log('Found passport number from pattern:', result.document_number);
    }
  }
  
  // Look for date pattern (DD MM YYYY)
  if (!result.birth_date) {
    const datePatterns = fullText.match(/(\d{2})[\s\.\/](\d{2})[\s\.\/](19\d{2}|20\d{2})/);
    if (datePatterns) {
      const [, day, month, year] = datePatterns;
      // Sanity check - birth year should be reasonable
      if (parseInt(year) > 1920 && parseInt(year) < 2020) {
        result.birth_date = `${year}-${month}-${day}`;
        result.confidence += 10;
        console.log('Found birth date from pattern:', result.birth_date);
      }
    }
  }
  
  // === METHOD 3: Try MRZ as fallback/supplement ===
  const mrzData = parseMRZ(text);
  if (mrzData) {
    // Fill in any missing fields from MRZ
    if (!result.last_name && mrzData.last_name) {
      result.last_name = mrzData.last_name;
      result.confidence += 15;
    }
    if (!result.first_name && mrzData.first_name) {
      result.first_name = mrzData.first_name;
      result.confidence += 15;
    }
    if (!result.birth_date && mrzData.birth_date) {
      result.birth_date = mrzData.birth_date;
      result.confidence += 10;
    }
    if (!result.document_number && mrzData.document_number) {
      result.document_number = mrzData.document_number;
      result.confidence += 10;
    }
    if (!result.nationality && mrzData.nationality) {
      result.nationality = mrzData.nationality;
    }
    if (!result.sex && mrzData.sex) {
      result.sex = mrzData.sex;
    }
  }
  
  // Default nationality for French passports
  if (!result.nationality && fullText.includes('FRANCE')) {
    result.nationality = 'FR';
  }
  
  // Cap confidence at 100
  result.confidence = Math.min(100, result.confidence);
  
  console.log('Final parsed result:', result);
  return result;
};

// Helper to capitalize a word
const capitalizeWord = (word) => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

// Parse MRZ lines - simplified fallback
const parseMRZ = (text) => {
  const result = {};
  
  // Look for MRZ patterns in text
  const upperText = text.toUpperCase().replace(/[^A-Z0-9<\n]/g, '');
  
  // Find line 2 pattern (more reliable): document number + country + dates
  const line2Match = upperText.match(/([A-Z0-9]{9})\d([A-Z]{3})(\d{6})\d([MF])(\d{6})/);
  if (line2Match) {
    result.document_number = line2Match[1].replace(/</g, '');
    result.nationality = COUNTRY_MAP[line2Match[2]] || line2Match[2];
    
    const birthRaw = line2Match[3];
    const yy = parseInt(birthRaw.substring(0, 2));
    const mm = birthRaw.substring(2, 4);
    const dd = birthRaw.substring(4, 6);
    const yyyy = yy > 30 ? 1900 + yy : 2000 + yy;
    result.birth_date = `${yyyy}-${mm}-${dd}`;
    
    result.sex = line2Match[4];
  }
  
  // Try to find names from MRZ line 1
  const line1Match = upperText.match(/P[<K]([A-Z]{3})([A-Z]+)[<K]{2,}([A-Z<K]+)/);
  if (line1Match) {
    result.nationality = COUNTRY_MAP[line1Match[1]] || result.nationality;
    result.last_name = capitalizeWord(line1Match[2]);
    const givenNames = line1Match[3].split(/[<K]+/).filter(n => n.length > 1);
    result.first_name = givenNames.map(n => capitalizeWord(n)).join(' ');
  }
  
  return Object.keys(result).length > 0 ? result : null;
};

export default function PassportScanner({ onScanComplete, onCancel }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState({});
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);

  // Initialize Tesseract worker
  const initWorker = async () => {
    if (!workerRef.current) {
      workerRef.current = await createWorker('eng', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        }
      });
      // Configure for MRZ recognition (uppercase only, specific characters)
      await workerRef.current.setParameters({
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<',
      });
    }
    return workerRef.current;
  };

  // Process image with OCR
  const processImage = async (imageData) => {
    setScanning(true);
    setProgress(0);
    setScanResult(null);

    try {
      const worker = await initWorker();
      
      // OCR the full passport image
      const { data: { text } } = await worker.recognize(imageData);
      
      console.log('OCR Raw output:', text);
      
      // Parse passport data from visual zone + MRZ
      const parsed = parsePassport(text);
      
      if (parsed && parsed.confidence > 20) {
        setScanResult({
          success: true,
          data: parsed,
          rawText: text
        });
        setEditedData({
          first_name: parsed.first_name || '',
          last_name: parsed.last_name || '',
          birth_date: parsed.birth_date || '',
          nationality: parsed.nationality || '',
          document_number: parsed.document_number || '',
        });
        toast.success(`Scan réussi (${parsed.confidence}% de confiance)`);
      } else {
        setScanResult({
          success: false,
          message: 'Données non détectées. Essayez avec une meilleure photo.',
          rawText: text
        });
        toast.error('Données non détectées');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      setScanResult({
        success: false,
        message: 'Erreur lors du scan. Réessayez.'
      });
      toast.error('Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  };

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      processImage(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Use extracted data
  const handleUseData = () => {
    if (scanResult?.success) {
      onScanComplete({
        ...editedData,
        document_type: 'PASSPORT',
        passportImage: imagePreview
      });
    }
  };

  // Reset and try again
  const handleReset = () => {
    setImagePreview(null);
    setScanResult(null);
    setEditMode(false);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4" data-testid="passport-scanner">
      {/* Scan Area */}
      {!imagePreview && !scanning && (
        <div className="border-2 border-dashed border-primary/50 rounded-xl p-8 text-center bg-primary/5">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Scan className="w-10 h-10 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Scanner un passeport</h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Prenez une photo de la page d'identité du passeport.<br />
            La zone MRZ (les 2 lignes en bas) doit être visible.
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
            id="passport-scan-input"
          />
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <label htmlFor="passport-scan-input">
              <Button type="button" asChild className="cursor-pointer">
                <span>
                  <Camera className="w-4 h-4 mr-2" />
                  Prendre une photo
                </span>
              </Button>
            </label>
            <label htmlFor="passport-scan-input">
              <Button type="button" variant="outline" asChild className="cursor-pointer">
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  Choisir une image
                </span>
              </Button>
            </label>
          </div>
          
          <div className="mt-6 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
            <p className="font-medium flex items-center justify-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Conseils pour un bon scan
            </p>
            <ul className="mt-2 text-left max-w-xs mx-auto space-y-1">
              <li>• Bonne lumière, sans reflets</li>
              <li>• Photo bien cadrée et nette</li>
              <li>• Zone MRZ complètement visible</li>
            </ul>
          </div>
        </div>
      )}

      {/* Scanning Progress */}
      {scanning && (
        <div className="text-center py-8">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="font-medium">Analyse en cours...</p>
          <div className="w-48 mx-auto mt-3 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">{progress}%</p>
        </div>
      )}

      {/* Results */}
      {scanResult && !scanning && (
        <div className="space-y-4">
          {/* Image preview */}
          <div className="relative">
            <img 
              src={imagePreview} 
              alt="Passeport scanné" 
              className="w-full max-h-48 object-contain rounded-lg border"
            />
            <Button 
              size="sm" 
              variant="secondary" 
              className="absolute top-2 right-2"
              onClick={handleReset}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Nouveau scan
            </Button>
          </div>

          {scanResult.success ? (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">
                    Données extraites
                  </span>
                  <Badge variant="outline" className="ml-auto text-green-700 border-green-300">
                    {scanResult.data.confidence}% confiance
                  </Badge>
                </div>

                {/* Editable fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Prénom</Label>
                    {editMode ? (
                      <Input 
                        value={editedData.first_name} 
                        onChange={(e) => setEditedData({...editedData, first_name: e.target.value})}
                        className="h-8"
                      />
                    ) : (
                      <p className="font-medium">{editedData.first_name || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nom</Label>
                    {editMode ? (
                      <Input 
                        value={editedData.last_name} 
                        onChange={(e) => setEditedData({...editedData, last_name: e.target.value})}
                        className="h-8"
                      />
                    ) : (
                      <p className="font-medium">{editedData.last_name || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date de naissance</Label>
                    {editMode ? (
                      <Input 
                        type="date"
                        value={editedData.birth_date} 
                        onChange={(e) => setEditedData({...editedData, birth_date: e.target.value})}
                        className="h-8"
                      />
                    ) : (
                      <p className="font-medium">{editedData.birth_date || '-'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nationalité</Label>
                    {editMode ? (
                      <Input 
                        value={editedData.nationality} 
                        onChange={(e) => setEditedData({...editedData, nationality: e.target.value})}
                        className="h-8"
                      />
                    ) : (
                      <p className="font-medium">{editedData.nationality || '-'}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">N° Passeport</Label>
                    {editMode ? (
                      <Input 
                        value={editedData.document_number} 
                        onChange={(e) => setEditedData({...editedData, document_number: e.target.value})}
                        className="h-8"
                      />
                    ) : (
                      <p className="font-medium font-mono">{editedData.document_number || '-'}</p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setEditMode(!editMode)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {editMode ? 'Terminer' : 'Corriger'}
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleUseData}
                    className="flex-1"
                    data-testid="use-scan-data-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Utiliser ces données
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4 text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="font-medium text-red-800 mb-2">{scanResult.message}</p>
                <p className="text-sm text-red-600 mb-4">
                  Assurez-vous que la zone MRZ (2 lignes en bas du passeport) est bien visible et nette.
                </p>
                <Button onClick={handleReset}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Réessayer
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Cancel button */}
      <div className="flex justify-center">
        <Button variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
      </div>
    </div>
  );
}
